/* LS header — desktop category panel, mobile drawer with a second level, and
   the mobile search row. Scoped to .ls-header-wrapper. */
(function () {
  const DESKTOP = window.matchMedia('(min-width: 990px)');

  class LsHeader {
    constructor(root) {
      this.root = root;
      this.header = root.querySelector('.ls-header');
      this.mega = root.querySelector('[id="LsHeaderMega"]');
      this.megaToggle = root.querySelector('[data-ls-mega-toggle]');
      this.drawer = root.querySelector('[id="LsHeaderDrawer"]');
      this.drawerToggle = root.querySelector('[data-ls-drawer-toggle]');
      this.panels = root.querySelector('[data-ls-drawer-level]');
      this.rootPanel = root.querySelector('.ls-header__panel--root');
      this.shopPanel = root.querySelector('.ls-header__panel--shop');
      this.searchToggle = root.querySelector('[data-ls-search-toggle]');
      this.mobileSearch = root.querySelector('[id="LsHeaderMobileSearch"]');

      this.bind();
      this.setHeight();
    }

    bind() {
      if (this.megaToggle) {
        // Hover and keyboard focus open the panel. Click is deliberately not
        // bound — the trigger is a link and has to keep navigating.
        const open = () => {
          if (!DESKTOP.matches) return;
          window.clearTimeout(this.megaCloseTimer);
          this.openMega();
        };
        this.megaToggle.addEventListener('mouseenter', open);
        this.megaToggle.addEventListener('focus', open);

        // Moving onto any other top-level link dismisses it. The pointer never
        // leaves .ls-header on that path, so mouseleave alone would not fire.
        this.root.querySelectorAll('.ls-header__nav-link').forEach((link) => {
          if (link === this.megaToggle) return;
          link.addEventListener('mouseenter', () => this.closeMega());
          link.addEventListener('focus', () => this.closeMega());
        });

        // Closing is keyed off the whole header, not the trigger, so the
        // pointer can travel down into the panel without it shutting on the
        // way; the delay covers the gap between them.
        this.header.addEventListener('mouseleave', () => {
          if (!DESKTOP.matches) return;
          this.megaCloseTimer = window.setTimeout(() => this.closeMega(), 200);
        });

        this.header.addEventListener('mouseenter', () => {
          window.clearTimeout(this.megaCloseTimer);
        });
      }

      // The cart drawer opens over the header, so the panel gets out of its way.
      document.addEventListener('click', (event) => {
        if (!(event.target instanceof Element)) return;
        if (!event.target.closest('#cart-icon-bubble, cart-drawer, #cart-notification')) return;
        this.closeMega();
      });

      if (this.drawerToggle) {
        this.drawerToggle.addEventListener('click', () => this.toggleDrawer());
      }

      if (this.searchToggle) {
        this.searchToggle.addEventListener('click', () => this.toggleSearch());
      }

      this.root.querySelectorAll('[data-ls-drawer-push]').forEach((button) => {
        button.addEventListener('click', () => this.setLevel('shop'));
      });

      this.root.querySelectorAll('[data-ls-drawer-pop]').forEach((button) => {
        button.addEventListener('click', () => this.setLevel('root'));
      });

      document.addEventListener('keydown', (event) => {
        if (event.key !== 'Escape') return;
        this.closeMega();
        this.closeDrawer();
        this.closeSearch();
      });

      // A click anywhere outside the header dismisses the category panel.
      document.addEventListener('click', (event) => {
        if (this.root.contains(event.target)) return;
        this.closeMega();
      });

      this.root.addEventListener('ls-header:collapse', () => {
        this.closeMega();
        this.closeDrawer();
      });

      DESKTOP.addEventListener('change', () => {
        this.closeMega();
        this.closeDrawer();
        this.closeSearch();
        this.setHeight();
      });

      window.addEventListener('resize', () => {
        this.setHeight();
        if (this.drawer && !this.drawer.hidden) this.setDrawerTop();
      });
    }

    /* The sticky header only publishes --header-height when it is enabled. */
    setHeight() {
      const section = this.root.closest('.section-header') || this.root;
      document.documentElement.style.setProperty('--header-height', `${section.offsetHeight}px`);
    }

    /* The drawer hangs off the header's real bottom edge on screen, which is
       not the same as its height: the announcement bar sits above it, so a
       fixed element at top:--header-height would ride up over the header and
       hide the close button. Body scroll is locked while the drawer is open,
       so measuring once per open is enough. */
    setDrawerTop() {
      const section = this.root.closest('.section-header') || this.root;
      const bottom = Math.max(0, Math.round(section.getBoundingClientRect().bottom));
      document.documentElement.style.setProperty('--ls-drawer-top', `${bottom}px`);
    }

    openMega() {
      if (!this.mega) return;
      this.mega.hidden = false;
      this.megaToggle.setAttribute('aria-expanded', 'true');
      this.header.classList.add('ls-header--open');
      this.setHeight();
    }

    closeMega() {
      if (!this.mega || this.mega.hidden) return;
      this.mega.hidden = true;
      this.megaToggle.setAttribute('aria-expanded', 'false');
      this.header.classList.remove('ls-header--open');
      this.setHeight();
    }

    toggleDrawer() {
      if (this.drawerToggle.getAttribute('aria-expanded') === 'true') {
        this.closeDrawer();
      } else {
        this.openDrawer();
      }
    }

    openDrawer() {
      if (!this.drawer) return;
      this.closeSearch();
      this.setDrawerTop();
      this.drawer.hidden = false;
      this.drawerToggle.setAttribute('aria-expanded', 'true');
      document.body.classList.add('overflow-hidden');
      this.setLevel('root');
    }

    closeDrawer() {
      if (!this.drawer || this.drawer.hidden) return;
      this.drawer.hidden = true;
      this.drawerToggle.setAttribute('aria-expanded', 'false');
      document.body.classList.remove('overflow-hidden');
    }

    setLevel(level) {
      if (!this.panels) return;
      this.panels.setAttribute('data-ls-drawer-level', level);
      if (this.rootPanel) this.rootPanel.setAttribute('aria-hidden', String(level !== 'root'));
      if (this.shopPanel) this.shopPanel.setAttribute('aria-hidden', String(level !== 'shop'));
    }

    toggleSearch() {
      if (this.searchToggle.getAttribute('aria-expanded') === 'true') {
        this.closeSearch();
      } else {
        this.openSearch();
      }
    }

    openSearch() {
      if (!this.mobileSearch) return;
      this.closeDrawer();
      this.mobileSearch.hidden = false;
      this.searchToggle.setAttribute('aria-expanded', 'true');
      this.setHeight();
      const input = this.mobileSearch.querySelector('input[type="search"]');
      if (input) input.focus();
    }

    closeSearch() {
      if (!this.mobileSearch || this.mobileSearch.hidden) return;
      this.mobileSearch.hidden = true;
      this.searchToggle.setAttribute('aria-expanded', 'false');
      this.setHeight();
    }
  }

  function init() {
    document.querySelectorAll('.ls-header-wrapper').forEach((root) => {
      if (root.dataset.lsHeaderReady) return;
      root.dataset.lsHeaderReady = 'true';
      new LsHeader(root);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Theme editor re-renders the section without a page load.
  document.addEventListener('shopify:section:load', init);
})();
